import { useRef, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Image,
  Loader,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Modal,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconCamera, IconPhoto, IconTrash, IconX, IconTag } from "@tabler/icons-react";
import { useUploadPhoto, useDeletePhoto, useUpdatePhoto, type Photo } from "@/hooks/usePhotos";
import { useProjectCostCodeOptions } from "@/hooks/useProjectCostCodes";

// API base URL for photo requests
const API_URL = import.meta.env.VITE_API_URL || "";

/**
 * Get the full URL for a photo, handling:
 * - R2 keys (companyId/projectId/photoId.extension)
 * - Relative API paths (/api/...)
 * - Full URLs (http://...)
 */
function getPhotoUrl(photo: Photo): string {
  // If it's already an absolute URL, use it
  if (photo.url.startsWith("http://") || photo.url.startsWith("https://")) {
    return photo.url;
  }

  // If it's an API path, prepend the API URL
  if (photo.url.startsWith("/api/")) {
    return `${API_URL}${photo.url}`;
  }

  // Otherwise, it's an R2 key - construct the API file serving URL
  // URL pattern: /api/projects/{projectId}/photos/{photoId}/file
  return `${API_URL}/api/projects/${photo.projectId}/photos/${photo.id}/file`;
}

interface PhotoCaptureProps {
  projectId: string;
  dailyLogId?: string;
  photos: Photo[];
  onPhotoAdded?: (photo: Photo) => void;
  onPhotoDeleted?: (photoId: string) => void;
  maxPhotos?: number;
  disabled?: boolean;
  showCostCodeSelector?: boolean;
}

interface PendingPhoto {
  id: string;
  file: File;
  preview: string;
  caption: string;
  uploading: boolean;
  error?: string;
}

export function PhotoCapture({
  projectId,
  dailyLogId,
  photos = [],
  onPhotoAdded,
  onPhotoDeleted,
  maxPhotos = 10,
  disabled = false,
  showCostCodeSelector = false,
}: PhotoCaptureProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([]);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [photoModalOpened, { open: openPhotoModal, close: closePhotoModal }] = useDisclosure(false);

  const uploadPhoto = useUploadPhoto(projectId);
  const deletePhoto = useDeletePhoto(projectId);
  const updatePhoto = useUpdatePhoto(projectId);

  // Fetch cost codes if selector is enabled
  const { data: costCodeData, isLoading: costCodesLoading } = useProjectCostCodeOptions(projectId);
  const costCodeOptions = costCodeData?.options || [];

  const totalPhotos = photos.length + pendingPhotos.length;
  const canAddMore = totalPhotos < maxPhotos;

  // Handle file selection (from gallery or camera)
  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const newPendingPhotos: PendingPhoto[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file) continue;

      // Check if we've hit the limit
      if (photos.length + pendingPhotos.length + newPendingPhotos.length >= maxPhotos) {
        notifications.show({
          title: "Photo limit reached",
          message: `Maximum ${maxPhotos} photos allowed`,
          color: "yellow",
        });
        break;
      }

      // Validate file type
      if (!file.type.startsWith("image/")) {
        notifications.show({
          title: "Invalid file",
          message: "Please select an image file",
          color: "red",
        });
        continue;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        notifications.show({
          title: "File too large",
          message: "Maximum file size is 10MB",
          color: "red",
        });
        continue;
      }

      // Create preview
      const preview = URL.createObjectURL(file);

      newPendingPhotos.push({
        id: crypto.randomUUID(),
        file,
        preview,
        caption: "",
        uploading: false,
      });
    }

    setPendingPhotos((prev) => [...prev, ...newPendingPhotos]);

    // Auto-upload each photo
    for (const pending of newPendingPhotos) {
      uploadPendingPhoto(pending);
    }
  };

  // Upload a pending photo
  const uploadPendingPhoto = async (pending: PendingPhoto) => {
    setPendingPhotos((prev) =>
      prev.map((p) => (p.id === pending.id ? { ...p, uploading: true } : p))
    );

    try {
      // Get location if available
      let locationLat: number | undefined;
      let locationLng: number | undefined;

      if ("geolocation" in navigator) {
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, {
              timeout: 5000,
              maximumAge: 60000,
            });
          });
          locationLat = position.coords.latitude;
          locationLng = position.coords.longitude;
        } catch {
          // Location not available, continue without it
        }
      }

      const result = await uploadPhoto.mutateAsync({
        file: pending.file,
        metadata: {
          dailyLogId,
          caption: pending.caption || undefined,
          takenAt: new Date().toISOString(),
          locationLat,
          locationLng,
        },
      });

      // Remove from pending
      setPendingPhotos((prev) => prev.filter((p) => p.id !== pending.id));

      // Clean up preview URL
      URL.revokeObjectURL(pending.preview);

      // Notify parent
      if (onPhotoAdded && result.photo) {
        onPhotoAdded(result.photo);
      }

      notifications.show({
        title: "Photo uploaded",
        message: "Photo saved successfully",
        color: "green",
      });
    } catch (error) {
      setPendingPhotos((prev) =>
        prev.map((p) =>
          p.id === pending.id
            ? { ...p, uploading: false, error: error instanceof Error ? error.message : "Upload failed" }
            : p
        )
      );

      notifications.show({
        title: "Upload failed",
        message: error instanceof Error ? error.message : "Failed to upload photo",
        color: "red",
      });
    }
  };

  // Remove a pending photo
  const removePendingPhoto = (pendingId: string) => {
    setPendingPhotos((prev) => {
      const photo = prev.find((p) => p.id === pendingId);
      if (photo) {
        URL.revokeObjectURL(photo.preview);
      }
      return prev.filter((p) => p.id !== pendingId);
    });
  };

  // Delete an uploaded photo
  const handleDeletePhoto = async (photoId: string) => {
    try {
      await deletePhoto.mutateAsync(photoId);
      if (onPhotoDeleted) {
        onPhotoDeleted(photoId);
      }
      closePhotoModal();
      notifications.show({
        title: "Photo deleted",
        message: "Photo removed successfully",
        color: "green",
      });
    } catch (error) {
      notifications.show({
        title: "Delete failed",
        message: error instanceof Error ? error.message : "Failed to delete photo",
        color: "red",
      });
    }
  };

  // Open camera
  const openCamera = () => {
    cameraInputRef.current?.click();
  };

  // Open gallery
  const openGallery = () => {
    fileInputRef.current?.click();
  };

  // View photo
  const viewPhoto = (photo: Photo) => {
    setSelectedPhoto(photo);
    openPhotoModal();
  };

  // Update photo cost code
  const handleCostCodeChange = async (photoId: string, costCodeId: string | null) => {
    try {
      await updatePhoto.mutateAsync({
        id: photoId,
        data: { costCodeId: costCodeId || undefined },
      });

      notifications.show({
        title: "Cost code updated",
        message: "Photo cost code updated successfully",
        color: "green",
      });
    } catch (error) {
      notifications.show({
        title: "Update failed",
        message: error instanceof Error ? error.message : "Failed to update cost code",
        color: "red",
      });
    }
  };

  return (
    <Stack gap="md">
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={(e) => handleFileSelect(e.target.files)}
        disabled={disabled || !canAddMore}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        onChange={(e) => handleFileSelect(e.target.files)}
        disabled={disabled || !canAddMore}
      />

      {/* Action buttons */}
      <Group gap="sm">
        <Button
          variant="light"
          leftSection={<IconCamera size={18} />}
          onClick={openCamera}
          disabled={disabled || !canAddMore}
          size="sm"
        >
          Take Photo
        </Button>
        <Button
          variant="light"
          leftSection={<IconPhoto size={18} />}
          onClick={openGallery}
          disabled={disabled || !canAddMore}
          size="sm"
        >
          Choose from Gallery
        </Button>
      </Group>

      {/* Photo count */}
      {totalPhotos > 0 && (
        <Text size="sm" c="dimmed">
          {totalPhotos} of {maxPhotos} photos
        </Text>
      )}

      {/* Photo grid */}
      {(photos.length > 0 || pendingPhotos.length > 0) && (
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="md">
          {/* Uploaded photos */}
          {photos.map((photo) => {
            // Find cost code label for badge display
            const costCodeLabel = photo.costCodeId
              ? costCodeOptions.find((opt) => opt.value === photo.costCodeId)?.label
              : null;

            return (
              <Stack key={photo.id} gap={4}>
                <Card
                  padding={0}
                  radius="sm"
                  style={{ cursor: "pointer", position: "relative" }}
                  onClick={() => viewPhoto(photo)}
                >
                  <Image
                    src={photo.thumbnailUrl || getPhotoUrl(photo)}
                    alt={photo.caption || "Photo"}
                    height={120}
                    fit="cover"
                    fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23999'%3EPhoto%3C/text%3E%3C/svg%3E"
                  />
                  {/* Cost code badge overlay */}
                  {showCostCodeSelector && costCodeLabel && (
                    <Box
                      style={{
                        position: "absolute",
                        bottom: 4,
                        left: 4,
                        right: 4,
                      }}
                    >
                      <Badge
                        size="xs"
                        variant="filled"
                        color="blue"
                        leftSection={<IconTag size={10} />}
                        style={{
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {costCodeLabel.split(" - ")[0]}
                      </Badge>
                    </Box>
                  )}
                </Card>

                {/* Cost code selector */}
                {showCostCodeSelector && (
                  <Select
                    size="xs"
                    placeholder="Cost code"
                    data={costCodeOptions}
                    value={photo.costCodeId}
                    onChange={(value) => handleCostCodeChange(photo.id, value)}
                    searchable
                    clearable
                    disabled={costCodesLoading || updatePhoto.isPending}
                    onClick={(e) => e.stopPropagation()}
                    styles={{
                      input: {
                        fontSize: "11px",
                        minHeight: "24px",
                        height: "24px",
                      },
                    }}
                  />
                )}
              </Stack>
            );
          })}

          {/* Pending photos */}
          {pendingPhotos.map((pending) => (
            <Card
              key={pending.id}
              padding={0}
              radius="sm"
              style={{ position: "relative", aspectRatio: "1" }}
            >
              <Image
                src={pending.preview}
                alt="Uploading..."
                height={100}
                fit="cover"
                style={{ opacity: pending.uploading ? 0.5 : 1 }}
              />
              {pending.uploading && (
                <Box
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <Loader size="sm" color="white" />
                </Box>
              )}
              {pending.error && (
                <Box
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: "rgba(255,0,0,0.8)",
                    padding: 4,
                  }}
                >
                  <Text size="xs" c="white" ta="center">
                    Failed
                  </Text>
                </Box>
              )}
              {!pending.uploading && (
                <ActionIcon
                  size="xs"
                  variant="filled"
                  color="red"
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    removePendingPhoto(pending.id);
                  }}
                >
                  <IconX size={12} />
                </ActionIcon>
              )}
            </Card>
          ))}
        </SimpleGrid>
      )}

      {/* Empty state */}
      {photos.length === 0 && pendingPhotos.length === 0 && (
        <Card
          padding="xl"
          withBorder
          style={{
            borderStyle: "dashed",
            cursor: disabled ? "default" : "pointer",
          }}
          onClick={!disabled ? openCamera : undefined}
        >
          <Stack gap="xs" align="center">
            <IconCamera size={32} color="var(--mantine-color-dimmed)" />
            <Text size="sm" c="dimmed" ta="center">
              Tap to take a photo or select from gallery
            </Text>
          </Stack>
        </Card>
      )}

      {/* Photo view modal */}
      <Modal
        opened={photoModalOpened}
        onClose={closePhotoModal}
        title="Photo"
        size="lg"
        centered
      >
        {selectedPhoto && (
          <Stack gap="md">
            <Image
              src={getPhotoUrl(selectedPhoto)}
              alt={selectedPhoto.caption || "Photo"}
              fit="contain"
              mah={400}
            />

            {selectedPhoto.caption && (
              <Text size="sm">{selectedPhoto.caption}</Text>
            )}

            {/* Cost code selector in modal */}
            {showCostCodeSelector && (
              <Select
                label="Cost Code"
                placeholder="Select cost code"
                data={costCodeOptions}
                value={selectedPhoto.costCodeId}
                onChange={(value) => handleCostCodeChange(selectedPhoto.id, value)}
                searchable
                clearable
                disabled={costCodesLoading || updatePhoto.isPending}
                leftSection={<IconTag size={16} />}
              />
            )}

            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                {new Date(selectedPhoto.takenAt).toLocaleString()}
              </Text>
              <Button
                color="red"
                variant="light"
                size="sm"
                leftSection={<IconTrash size={16} />}
                onClick={() => handleDeletePhoto(selectedPhoto.id)}
                loading={deletePhoto.isPending}
              >
                Delete
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

export default PhotoCapture;
